import { assertSessionVersion, getSessionVersion } from "@/shared/lib/auth"
import { isNetworkError, toNetworkError } from "../errors"
import { extractPathname, noteNetworkFailure } from "./telemetry"

const REQUEST_TIMEOUT_MS = 5_000
const BODYLESS_STATUSES = new Set([101, 204, 205, 304])

export function deadlineFetch(ms: number): typeof fetch {
	return (input, init) => networkAwareFetch(input, init, ms)
}

function timeoutError(ms: number): DOMException {
	return new DOMException(`Request timed out after ${ms}ms`, "TimeoutError")
}

function isTimeoutError(cause: unknown): boolean {
	return (
		typeof cause === "object" &&
		cause !== null &&
		"name" in cause &&
		cause.name === "TimeoutError"
	)
}

function isEventStream(response: Response): boolean {
	return (
		response.headers.get("content-type")?.includes("text/event-stream") ?? false
	)
}

async function bufferResponse(response: Response): Promise<Response> {
	if (response.body === null || BODYLESS_STATUSES.has(response.status)) {
		return response
	}
	const body = await response.arrayBuffer()
	return new Response(body, {
		status: response.status,
		statusText: response.statusText,
		headers: response.headers,
	})
}

function describeInput(input: RequestInfo | URL): {
	url: string
	method: string
	signal: AbortSignal | undefined
} {
	if (input instanceof Request) {
		return { url: input.url, method: input.method, signal: input.signal }
	}
	return { url: String(input), method: "GET", signal: undefined }
}

export async function networkAwareFetch(
	input: RequestInfo | URL,
	init?: RequestInit,
	timeoutMs: number = REQUEST_TIMEOUT_MS,
): Promise<Response> {
	const version = getSessionVersion()
	const target = describeInput(input)
	const method = init?.method ?? target.method
	const path = extractPathname(target.url)
	const outer = init?.signal ?? target.signal
	const controller = new AbortController()
	const forwardAbort = () => controller.abort(outer?.reason)
	if (outer?.aborted) forwardAbort()
	else outer?.addEventListener("abort", forwardAbort, { once: true })
	const timer = setTimeout(
		() => controller.abort(timeoutError(timeoutMs)),
		timeoutMs,
	)
	let streaming = false
	try {
		const response = await fetch(input, { ...init, signal: controller.signal })
		clearTimeout(timer)
		assertSessionVersion(version)
		if (isEventStream(response)) {
			streaming = true
			return response
		}
		const buffered = await bufferResponse(response)
		assertSessionVersion(version)
		return buffered
	} catch (cause) {
		assertSessionVersion(version)
		const timedOut = isTimeoutError(controller.signal.reason)
		if (outer?.aborted && !timedOut) throw cause
		let failure: unknown = cause
		if (timedOut) failure = controller.signal.reason
		const error = toNetworkError(failure, { path, method })
		if (isNetworkError(error)) {
			noteNetworkFailure(method, path, timedOut)
		}
		throw error
	} finally {
		clearTimeout(timer)
		if (!streaming) outer?.removeEventListener("abort", forwardAbort)
	}
}
